import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import { RiFullscreenExitLine, RiFullscreenLine } from "@remixicon/react";
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useError } from "../../../contexts/ErrorContext";
import useApi from "../../../hooks/useGaneshApi";
import ReviewPlacesRecurrentAddresses from "./ReviewPlacesRecurrentAddresses";
import ReviewPlacesRecurrentPostalCodes from "./ReviewPlacesRecurrentPostalCodes";

interface PlacesReviewGeneralProps {
  brand_id: string;
  brand_name: string;
}

const PlacesReviewGeneral: React.FC<PlacesReviewGeneralProps> = ({
  brand_id,
  brand_name,
}) => {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [loadingCountries, setLoadingCountries] = useState(true);
  const [brandCountries, setBrandCountries] = useState<any[]>([]);
  const [selectedCountryCode, setSelectedCountryCode] = useState<string | null>(
    null
  );

  const { showError } = useError();
  const { apiRequest } = useApi();
  const location = useLocation();
  const navigate = useNavigate();

  const toggleFullscreen = () => {
    setIsFullscreen((prev) => !prev);
  };

  // Fetch countries
  useEffect(() => {
    if (brand_id) {
      const fetchBrandCountries = async () => {
        try {
          const response = await apiRequest<any>(
            `/brand_utils/brand_countries/${brand_id}`,
            {
              method: "GET",
            }
          );
          if (response.data) {
            const sortedCountries = [...response.data].sort((a, b) =>
              a.country_code.localeCompare(b.country_code)
            );
            setBrandCountries(sortedCountries);

            // Check if a country code exists in the query params
            const params = new URLSearchParams(location.search);
            const countryCode = params.get("country_code");
            if (
              countryCode &&
              sortedCountries.some((c) => c.country_code === countryCode)
            ) {
              setSelectedCountryCode(countryCode);
            }
          }
        } catch (error) {
          console.error(error);
          showError(
            `Failed to fetch countries for metadata review dropdown. Brand ID: ${brand_id}.`
          );
        } finally {
          setLoadingCountries(false);
        }
      };
      fetchBrandCountries();
    }
  }, [brand_id, location.search]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const countryCode = params.get("country");

    // Set selected country from URL if valid
    if (countryCode && brandCountries.length > 0) {
      const matchingCountry = brandCountries.find(
        (c) => c.country_code.toLowerCase() === countryCode.toLowerCase()
      );

      if (matchingCountry) {
        setSelectedCountryCode(matchingCountry.country_code);
      }
    }
  }, [location.search, brandCountries]);

  return (
    <Box
      sx={
        isFullscreen
          ? {
              position: "fixed",
              top: 0,
              left: 0,
              width: "100vw",
              height: "100vh",
              backgroundColor: "#fff",
              overflow: "auto",
              p: 5,
              pt: 0,
            }
          : {
              position: "relative",
              width: "auto",
              height: "auto",
              minHeight: "500px",
              overflow: "hidden",
              border: "1px solid #ccc",
              borderRadius: "4px",
              p: 5,
              mt: 2,
              backgroundColor: "#fff",
            }
      }
    >
      {/* Top Bar */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
          ...(isFullscreen && {
            position: "sticky",
            top: 0,
            backgroundColor: "#fff",
            width: "100%",
            zIndex: 1000,
            p: 2,
          }),
        }}
      >
        <Typography variant="h5">
          {isFullscreen
            ? `General Brand Places Review: ${brand_name} - ${
                selectedCountryCode?.toUpperCase() ||
                "Select an country to review"
              }`
            : ""}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          {/* Open Kuebiko Button */}
          <Button
            variant="outlined"
            color="primary"
            startIcon={
              <img
                src="/images/dataplor_logo.svg"
                alt="dataplor logo"
                style={{ width: 20, height: 20 }}
              />
            }
            onClick={() =>
              window.open(
                `https://kuebiko.dataplor.com/brands/${brand_id}/review`,
                "_blank"
              )
            }
          >
            Open Kuebiko
          </Button>
          {/* Fullscreen Toggle Button */}
          <IconButton onClick={toggleFullscreen} sx={{ zIndex: 500 }}>
            {isFullscreen ? <RiFullscreenExitLine /> : <RiFullscreenLine />}
          </IconButton>
        </Box>
      </Box>

      {/* Main Content */}
      <Box sx={{ mb: 2, pl: 2, pr: 2 }}>
        <Box>
          <Typography variant="h6" gutterBottom>
            General Brand Places Review
          </Typography>
        </Box>

        <Box sx={{ mt: 5 }}>
          <Box sx={{ mt: 5 }}>
            <Typography variant="h6" gutterBottom>
              Review Brand POI Data by Country
            </Typography>
            <Typography paragraph sx={{ fontStyle: "italic" }}>
              Iterate through one country at a time. Select a country below to
              update the data for review.
            </Typography>
          </Box>
          {/* Country Code Dropdown */}
          <Box sx={{ mt: 2, mb: 5, width: 300 }}>
            {loadingCountries ? (
              <CircularProgress />
            ) : (
              <Autocomplete
                options={brandCountries}
                getOptionLabel={(option) =>
                  `${option.country_code.toUpperCase()}: ${option.country_name}`
                }
                filterOptions={(options, { inputValue }) =>
                  options.filter(
                    (option) =>
                      option.country_code
                        .toLowerCase()
                        .includes(inputValue.toLowerCase()) ||
                      option.country_name
                        .toLowerCase()
                        .includes(inputValue.toLowerCase())
                  )
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select Country"
                    variant="outlined"
                  />
                )}
                value={
                  brandCountries.length > 0
                    ? brandCountries.find(
                        (country) =>
                          country.country_code === selectedCountryCode
                      ) || null
                    : null
                }
                onChange={(event, newValue) => {
                  if (newValue) {
                    setSelectedCountryCode(newValue.country_code);
                    navigate(
                      { search: `?country=${newValue.country_code}` },
                      { replace: true }
                    );
                  } else {
                    setSelectedCountryCode(null);
                    navigate({ search: "" }, { replace: true });
                  }
                }}
                isOptionEqualToValue={(option, value) =>
                  option.country_code === value.country_code
                }
              />
            )}
          </Box>
          {selectedCountryCode && (
            <Box>
              <ReviewPlacesRecurrentAddresses
                brand_id={brand_id}
                country_code={selectedCountryCode}
              />
              <ReviewPlacesRecurrentPostalCodes
                brand_id={brand_id}
                country_code={selectedCountryCode}
              />
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default PlacesReviewGeneral;
